import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import { db } from '../../database/schema.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { activityStorage } from '../../storage/activity-storage.js'
import { processTrack } from '../../services/track/track-processing-service.js'
import { assembleTrack, stripLegacyInterpolation } from '../../services/track/track-edit.js'
import { computeUncertaintyCorridor } from '../../services/track/uncertainty-corridor.js'
import type { ProcessedTrack } from '../../services/track/track-processing-service.js'
import type { Feature } from '../../schemas/feature.js'
import type { ActivityTrackInfo } from '../map/map-context-popup.js'
import { haversineDistance } from '../map/geo-utils.js'
import { timelineService, type TimelineListener } from '../../services/timeline/timeline-service.js'
import '../map/cairn-map.js'

@customElement('activity-map-page')
export class ActivityMapPage extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .header {
      display: flex;
      align-items: center;
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      flex-shrink: 0;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      margin-right: var(--cairn-spacing-sm, 8px);
      color: var(--cairn-text-color, #1e293b);
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .header-title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    cairn-map {
      flex: 1;
      min-height: 0;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      font-size: 24px;
      line-height: 1;
      display: inline-block;
    }

    .track-controls {
      position: absolute;
      top: 140px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: row;
      z-index: 10;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      border-radius: 12px;
      overflow: hidden;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .track-btn {
      background: var(--cairn-surface-color, #ffffff);
      border: none;
      border-radius: 0;
      width: 40px;
      height: 40px;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.15s ease;
    }

    .track-btn:first-child {
      border-start-start-radius: 12px;
      border-end-start-radius: 12px;
    }

    .track-btn:last-child {
      border-start-end-radius: 12px;
      border-end-end-radius: 12px;
    }

    .track-btn:not(:first-child) {
      border-left: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .track-btn.active {
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }

      .track-controls {
        top: 80px;
      }
    }
  `

  @property({ type: String }) activityId?: string
  @state() private activityName = ''
  @state() private loaded = false
  private linkedPlanId?: string | undefined
  private activityTimeRange?: { start: Date; end: Date }
  private processedTrack: ProcessedTrack | null = null
  private rawCoords: GeoJSON.Position[] = []
  private corridorPolygon: GeoJSON.Position[][][] = []
  private pendingMap: maplibregl.Map | null = null
  private trackOverlayAdded = false
  @state() private showNormalized = true
  @state() private showRaw = false
  @state() private showCorridor = false

  // Precomputed arrays for fast nearest-point lookup
  private cumDist: number[] = []
  private cumGain: number[] = []
  private cumLoss: number[] = []

  // Timeline position marker
  private timelineMarker: maplibregl.Marker | null = null
  private timelineListener: TimelineListener = state => {
    this.updateTimelineMarker(state.currentTime)
  }

  override connectedCallback(): void {
    super.connectedCallback()
    if (this.activityId) void this.load()
    timelineService.addListener(this.timelineListener)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    timelineService.removeListener(this.timelineListener)
    this.timelineMarker?.remove()
    this.timelineMarker = null
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('activityId') && this.activityId) void this.load()
    if (changed.has('showNormalized') || changed.has('showRaw') || changed.has('showCorridor')) {
      this.updateTrackLayerVisibility()
    }
  }

  private updateTrackLayerVisibility(): void {
    if (!this.pendingMap || !this.trackOverlayAdded) return
    const v = (show: boolean): 'visible' | 'none' => (show ? 'visible' : 'none')
    this.pendingMap.setLayoutProperty('activity-track-line', 'visibility', v(this.showNormalized))
    this.pendingMap.setLayoutProperty('activity-track-casing', 'visibility', v(this.showNormalized))
    this.pendingMap.setLayoutProperty('activity-raw-line', 'visibility', v(this.showRaw))
    this.pendingMap.setLayoutProperty('activity-corridor-fill', 'visibility', v(this.showCorridor))
    this.pendingMap.setLayoutProperty(
      'activity-corridor-outline',
      'visibility',
      v(this.showCorridor)
    )
  }

  private async load(): Promise<void> {
    if (!this.activityId) return

    const activity = await db.activities.get(this.activityId)
    this.activityName = activity?.name ?? 'Activity Map'

    this.linkedPlanId = await activityStorage.getLinkedPlanId(this.activityId)

    const features = await featureStorage.getFeaturesForEntity('activity', this.activityId)
    let trackFeature: Feature | undefined = features.find(
      f => f.type === 'track' && f.geometry.type === 'LineString'
    )
    if (trackFeature?.geometry.type !== 'LineString') {
      this.loaded = true
      return
    }
    // Migrate legacy in-geometry fill points, then assemble raw + fill overlay
    // (matches activity-detail so the expanded map shows the corrected track).
    if (await stripLegacyInterpolation(trackFeature)) {
      trackFeature = (await db.features.get(trackFeature.id)) ?? trackFeature
    }

    const props = (trackFeature.properties ?? {}) as Record<string, unknown>
    const coordAccuracies = props.coordAccuracies as (number | null)[] | undefined
    const coordActivities = props.coordActivities as (string | null)[] | undefined
    if (!coordAccuracies || !coordActivities) {
      this.loaded = true
      return
    }

    const t = assembleTrack(trackFeature)
    this.rawCoords = (trackFeature.geometry as GeoJSON.LineString).coordinates
    this.processedTrack = processTrack(
      t.coords,
      t.coordTimes,
      t.coordAccuracies,
      t.coordActivities,
      t.coordSpeeds,
      t.coordDemElevations,
      {},
      t.interpolated
    )
    // Corridor uses the sparse display centerline + per-bin scatter: it's a fuzzy
    // band, and unioning a polygon per cleaned point (~2.4k) is ~20× slower for no
    // visible difference (the band is far wider than the ≤7 m line offset).
    this.corridorPolygon = computeUncertaintyCorridor(
      this.processedTrack.displayCoords,
      this.processedTrack.displayScatter
    )
    this.precomputeTrackStats()

    const ct = this.processedTrack.cleanedTimestamps
    if (ct.length >= 2) {
      this.activityTimeRange = { start: new Date(ct[0]), end: new Date(ct[ct.length - 1]) }
    }

    this.loaded = true
    this.tryAddTrackOverlay()
  }

  private precomputeTrackStats(): void {
    if (!this.processedTrack) return
    const coords = this.processedTrack.cleanedCoords
    const n = coords.length

    this.cumDist = new Array(n).fill(0) as number[]
    this.cumGain = new Array(n).fill(0) as number[]
    this.cumLoss = new Array(n).fill(0) as number[]

    for (let i = 1; i < n; i++) {
      this.cumDist[i] =
        this.cumDist[i - 1] +
        haversineDistance(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])

      const prevElev = coords[i - 1].length >= 3 ? coords[i - 1][2] : null
      const curElev = coords[i].length >= 3 ? coords[i][2] : null
      if (prevElev !== null && curElev !== null) {
        const dz = curElev - prevElev
        this.cumGain[i] = this.cumGain[i - 1] + Math.max(0, dz)
        this.cumLoss[i] = this.cumLoss[i - 1] + Math.max(0, -dz)
      } else {
        this.cumGain[i] = this.cumGain[i - 1]
        this.cumLoss[i] = this.cumLoss[i - 1]
      }
    }
  }

  private findNearestTrackIndex(lng: number, lat: number): number {
    if (!this.processedTrack) return 0
    const coords = this.processedTrack.cleanedCoords
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < coords.length; i++) {
      const d = haversineDistance(lat, lng, coords[i][1], coords[i][0])
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    return bestIdx
  }

  private getTrackInfoAtIndex(idx: number): ActivityTrackInfo | null {
    if (!this.processedTrack) return null
    const coords = this.processedTrack.cleanedCoords
    const timestamps = this.processedTrack.cleanedTimestamps

    const elev = coords[idx].length >= 3 ? coords[idx][2] : null
    const elapsedS = timestamps.length >= 2 ? (timestamps[idx] - timestamps[0]) / 1000 : 0

    // Check if this point is in a rest segment
    let rest: { duration: number; radius: number } | null = null
    for (const seg of this.processedTrack.restSegments) {
      if (idx >= seg.startIndex && idx <= seg.endIndex) {
        rest = { duration: seg.duration, radius: seg.radius }
        break
      }
    }

    return {
      distanceM: this.cumDist[idx] ?? 0,
      elevationGainM: this.cumGain[idx] ?? 0,
      elevationLossM: this.cumLoss[idx] ?? 0,
      elevationM: elev,
      elapsedS,
      rest,
    }
  }

  private handleContextPopup(
    e: CustomEvent<{ popup: HTMLElement; lngLat: maplibregl.LngLat }>
  ): void {
    if (!this.processedTrack) return
    const { popup, lngLat } = e.detail
    const idx = this.findNearestTrackIndex(lngLat.lng, lngLat.lat)
    const info = this.getTrackInfoAtIndex(idx)
    if (info) {
      ;(popup as unknown as { activityTrackInfo: ActivityTrackInfo }).activityTrackInfo = info
    }
  }

  private interpolateTimelinePosition(
    currentTime: Date
  ): { pos: [number, number]; idx: number } | null {
    if (!this.processedTrack) return null
    const ts = currentTime.getTime()
    const timestamps = this.processedTrack.cleanedTimestamps
    if (timestamps.length < 2) return null

    const tStart = timestamps[0]
    const tEnd = timestamps[timestamps.length - 1]
    if (ts < tStart || ts > tEnd) return null

    const coords = this.processedTrack.cleanedCoords
    let prev = 0
    for (let i = 1; i < timestamps.length; i++) {
      if (timestamps[i] >= ts) {
        const tRange = timestamps[i] - timestamps[prev]
        const t = tRange > 0 ? (ts - timestamps[prev]) / tRange : 0
        const lng = coords[prev][0] + t * (coords[i][0] - coords[prev][0])
        const lat = coords[prev][1] + t * (coords[i][1] - coords[prev][1])
        return { pos: [lng, lat], idx: i }
      }
      prev = i
    }
    const last = coords[coords.length - 1]
    return { pos: [last[0], last[1]], idx: coords.length - 1 }
  }

  /** Compute bearing by looking ~100m ahead on the track from the given index */
  private computeLookAheadBearing(lng: number, lat: number, fromIdx: number): number {
    if (!this.processedTrack) return 0
    const coords = this.processedTrack.cleanedCoords
    const cumDist = this.cumDist
    const startDist = cumDist[fromIdx] ?? 0

    // Find a point ~100m ahead for a stable bearing
    for (let i = fromIdx + 1; i < coords.length; i++) {
      if (cumDist[i] - startDist >= 100) {
        return ActivityMapPage.bearing(lat, lng, coords[i][1], coords[i][0])
      }
    }
    // Not enough track left — use last point
    const last = coords[coords.length - 1]
    return ActivityMapPage.bearing(lat, lng, last[1], last[0])
  }

  private static bearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (d: number): number => (d * Math.PI) / 180
    const toDeg = (r: number): number => (r * 180) / Math.PI
    const dLng = toRad(lng2 - lng1)
    const y = Math.sin(dLng) * Math.cos(toRad(lat2))
    const x =
      Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
      Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng)
    return (toDeg(Math.atan2(y, x)) + 360) % 360
  }

  private updateTimelineMarker(currentTime: Date): void {
    if (!this.pendingMap) return
    const result = this.interpolateTimelinePosition(currentTime)

    if (!result) {
      this.timelineMarker?.remove()
      this.timelineMarker = null
      return
    }

    const { pos, idx } = result
    this.showTimelineMarkerAt(pos[0], pos[1])

    const bearing = this.computeLookAheadBearing(pos[0], pos[1], idx)
    const cairnMap = this.shadowRoot?.querySelector('cairn-map') as
      | (HTMLElement & { followTo: (lng: number, lat: number, bearing?: number) => void })
      | null
    cairnMap?.followTo(pos[0], pos[1], bearing)
  }

  private showTimelineMarkerAt(lng: number, lat: number): void {
    if (!this.pendingMap) return
    if (this.timelineMarker) {
      this.timelineMarker.setLngLat([lng, lat])
    } else {
      const el = document.createElement('div')
      el.style.width = '14px'
      el.style.height = '14px'
      el.style.borderRadius = '50%'
      el.style.background = '#facc15'
      el.style.border = '2px solid white'
      el.style.boxShadow = '0 0 4px rgba(0,0,0,0.5)'
      this.timelineMarker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(this.pendingMap)
    }
  }

  private handleMapReady(e: CustomEvent<{ map: maplibregl.Map }>): void {
    this.pendingMap = e.detail.map
    this.tryAddTrackOverlay()
    // Set initial follow position to track start
    if (this.processedTrack?.cleanedCoords.length) {
      const start = this.processedTrack.cleanedCoords[0]
      const cairnMap = this.shadowRoot?.querySelector('cairn-map') as
        | (HTMLElement & { followPosition?: [number, number] })
        | null
      if (cairnMap) cairnMap.followPosition = [start[0], start[1]]
    }
  }

  private tryAddTrackOverlay(): void {
    if (this.trackOverlayAdded || !this.processedTrack || !this.pendingMap) return
    this.trackOverlayAdded = true

    const map = this.pendingMap
    // Render the cleaned (Kalman-smoothed) track as the primary line; it follows
    // the real path more faithfully than the old median resample.
    const trackCoords = this.processedTrack.cleanedCoords
    const restMidpoints = this.processedTrack.restSegments.map(r => r.midpoint)

    if (trackCoords.length < 2) return

    const trackGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: trackCoords },
        },
      ],
    }

    const restGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: restMidpoints.map(pt => ({
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'Point' as const, coordinates: pt },
      })),
    }

    const corridorGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        this.corridorPolygon.length >= 1
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'MultiPolygon', coordinates: this.corridorPolygon },
              },
            ]
          : [],
    }

    const rawGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        this.rawCoords.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: this.rawCoords },
              },
            ]
          : [],
    }

    map.addSource('activity-corridor', { type: 'geojson', data: corridorGeoJSON })
    map.addSource('activity-raw', { type: 'geojson', data: rawGeoJSON })
    map.addSource('activity-track', { type: 'geojson', data: trackGeoJSON })
    map.addSource('activity-rests', { type: 'geojson', data: restGeoJSON })

    map.addLayer({
      id: 'activity-corridor-fill',
      type: 'fill',
      source: 'activity-corridor',
      paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.35 },
      layout: { visibility: 'none' },
    })
    map.addLayer({
      id: 'activity-corridor-outline',
      type: 'line',
      source: 'activity-corridor',
      paint: { 'line-color': '#3b82f6', 'line-width': 1.5, 'line-opacity': 0.5 },
      layout: { visibility: 'none' },
    })
    map.addLayer({
      id: 'activity-raw-line',
      type: 'line',
      source: 'activity-raw',
      paint: { 'line-color': '#ef4444', 'line-width': 2, 'line-opacity': 0.7 },
      layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
    })
    map.addLayer({
      id: 'activity-track-casing',
      type: 'line',
      source: 'activity-track',
      paint: { 'line-color': '#ffffff', 'line-width': 5 },
    })
    map.addLayer({
      id: 'activity-track-line',
      type: 'line',
      source: 'activity-track',
      paint: { 'line-color': '#2563eb', 'line-width': 3 },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    })
    map.addLayer({
      id: 'activity-rest-points',
      type: 'circle',
      source: 'activity-rests',
      paint: {
        'circle-radius': 5,
        'circle-color': '#f59e0b',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    })

    const bounds = new maplibregl.LngLatBounds()
    for (const coord of trackCoords) {
      bounds.extend([coord[0], coord[1]])
    }
    map.fitBounds(bounds, { padding: 60, duration: 0 })
  }

  override render(): unknown {
    return html`
      <div class="header">
        <button class="back-button" @click=${() => window.history.back()}>
          <span class="material-icons">arrow_back</span>
        </button>
        <h1 class="header-title">${this.activityName}</h1>
      </div>
      ${this.loaded
        ? html`<cairn-map
              .planId=${this.linkedPlanId}
              .timelineRange=${this.activityTimeRange}
              ?viewOnly=${true}
              @map-ready=${this.handleMapReady}
              @context-popup-showing=${this.handleContextPopup}
            ></cairn-map>
            <div class="track-controls">
              <button
                class="track-btn ${this.showNormalized ? 'active' : ''}"
                @click=${() => {
                  this.showNormalized = !this.showNormalized
                }}
                title="Normalized track"
              >
                <span class="material-icons">show_chart</span>
              </button>
              <button
                class="track-btn ${this.showRaw ? 'active' : ''}"
                @click=${() => {
                  this.showRaw = !this.showRaw
                }}
                title="Raw GPS track"
              >
                <span class="material-icons">gesture</span>
              </button>
              <button
                class="track-btn ${this.showCorridor ? 'active' : ''}"
                @click=${() => {
                  this.showCorridor = !this.showCorridor
                }}
                title="Uncertainty corridor"
              >
                <span class="material-icons">blur_on</span>
              </button>
            </div>`
        : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-map-page': ActivityMapPage
  }
}
